import React, { useCallback, useState } from 'react'
import { useTranslation } from 'react-i18next'
import {
  Stack,
  CardHeader,
  Divider,
  Tooltip,
  IconButton,
  Typography,
  Box
} from '@mui/material'
import { Payments as PaymentsIcon } from '@mui/icons-material'
import { useNavigate } from 'react-router-dom'

import { LoansList } from 'components'
import { Order_By, useGetBaseLoansQuery } from 'api/generated'
import { formatCurrency } from 'utils/currency'
import { getLoansAggregateWhere, getLoansFilterPath } from 'utils/chart'
import { DictionariesQueryProps } from 'screens/Authenticated/Dashboard/Admin'
import { PaginationModelType, defaultPaginationModel } from 'utils/dataGrid'

const LoansChart: React.FC<DictionariesQueryProps> = ({
  dictionariesQuery,
  locationId,
  userId,
  dateRange
}: DictionariesQueryProps) => {
  const { t } = useTranslation()
  const navigate = useNavigate()
  const dictionariesData = dictionariesQuery?.data
  const [paginationModel, setPaginationModel] = useState<PaginationModelType>(
    defaultPaginationModel
  )
  const { page, pageSize } = paginationModel
  const { data, loading, error, refetch } = useGetBaseLoansQuery({
    variables: {
      where: getLoansAggregateWhere(dateRange, userId, locationId),
      limit: pageSize,
      offset: pageSize * page,
      order_by: { created_at: Order_By.Desc }
    }
  })

  const aggregate = data?.loansAggregate?.aggregate
  const aggregateSum = aggregate?.sum
  const totalCount = aggregate?.totalCount

  const handleShowLoansInDetailTable = useCallback(() => {
    const selectedUser = dictionariesData?.users?.find(
      user => user?.id === userId
    )
    const selectedLocation = dictionariesData?.locations?.find(
      location => location?.id === locationId
    )
    const paymentsFilterPath = getLoansFilterPath(
      dateRange,
      selectedUser?.full_name,
      selectedLocation?.name
    )

    navigate(paymentsFilterPath)
  }, [dateRange, dictionariesData, userId, locationId, navigate])

  return (
    <>
      <CardHeader
        title={t('loans')}
        subheader={
          <Stack
            direction='row'
            spacing={1}
            divider={<Divider orientation='vertical' flexItem />}
          >
            <Tooltip title={t('totalLoans')}>
              <Typography variant='body1'>{totalCount || 0}</Typography>
            </Tooltip>

            <Tooltip title={t('totalAmount')}>
              <Typography variant='body1'>
                {aggregateSum
                  ? formatCurrency({
                      amount: aggregateSum?.amount
                    })
                  : ''}
              </Typography>
            </Tooltip>
          </Stack>
        }
        action={
          <Tooltip title={t('showInDetail')}>
            <IconButton onClick={handleShowLoansInDetailTable}>
              <PaymentsIcon />
            </IconButton>
          </Tooltip>
        }
      />

      <Box flex={1}>
        <LoansList
          loading={loading}
          refetch={refetch}
          error={error}
          data={data}
          paginationModel={paginationModel}
          setPaginationModel={setPaginationModel}
        />
      </Box>
    </>
  )
}

export default LoansChart
